import { useCallback, useRef, useState } from 'react';

/**
 * Wraps an async action so a button can never fire it twice concurrently.
 *
 * Returns [run, loading]:
 *  - `loading` drives the button's spinner + disabled state.
 *  - `run(...args)` is IGNORED while a previous call is still in flight (in-flight
 *    debounce) — this is the guard against double-taps / rapid clicks sending
 *    duplicate API requests. It also debounces a trailing-edge minimum gap so a
 *    fast double-tap right after completion is swallowed too.
 *
 * Every mutating button in the app routes through this (or <AsyncButton>, which
 * uses it internally), so no action can be submitted twice at once.
 */
export function useAsyncAction(fn, { minGapMs = 400, onError } = {}) {
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);
  const lastDone = useRef(0);

  const run = useCallback(
    async (...args) => {
      if (inFlight.current) return undefined; // already running → ignore the click
      if (Date.now() - lastDone.current < minGapMs) return undefined; // trailing debounce
      inFlight.current = true;
      setLoading(true);
      try {
        return await fn(...args);
      } catch (err) {
        if (onError) onError(err);
        else throw err;
        return undefined;
      } finally {
        inFlight.current = false;
        lastDone.current = Date.now();
        setLoading(false);
      }
    },
    [fn, minGapMs, onError]
  );

  return [run, loading];
}
